import axios from "axios";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { start, done } from "../../store/loaderSlice";
import { snackbarStart } from "../../store/SnackbarSlice";
import { useTranslation } from "react-i18next";
import { UPLOAD_IMAGE, GET_CATEGORIES, TESTS_API } from "../../constant";

// Universal component for creating and editing tests
export default function TestForm({
  setFormStatus,
  testId,
  refreshData,
  setSelectedIds,
}) {
  const isEditMode = Boolean(testId);

  const [test, setTest] = useState({
    title: "",
    description: "",
    totalTimeSeconds: 7200,
    categoryId: "",
    questions: [],
    isPublic: false,
  });

  const [activeTab, setActiveTab] = useState("basic");
  const [availableCategories, setAvailableCategories] = useState([]);
  const { token } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isFetchingTest, setIsFetchingTest] = useState(isEditMode);

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchTestDetails();
    } else {
      // Create mode reset
      setTest({
        title: "",
        description: "",
        totalTimeSeconds: 6000,
        categoryId: "",
        questions: [],
        isPublic: false,
      });
    }
  }, [testId, isEditMode]);

  const fetchTestDetails = async () => {
    dispatch(start());
    setIsFetchingTest(true);
    try {
      const { data } = await axios({
        method: "get",
        url: `${TESTS_API}/${testId}`,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
      });

      setTest({
        title: data.title || "",
        description: data.description || "",
        totalTimeSeconds: data.totalTimeSeconds || 7200,
        categoryId: data.categoryId || data.category?._id || "",
        questions: data.questions || [],
        isPublic: data.isPublic || false,
      });
      dispatch(done());
    } catch (err) {
      console.error("Error fetching test details:", err);
      dispatch(done());
      dispatch(
        snackbarStart({
          text: t("somethingWentWrong"),
          severity: "error",
        }),
      );
      setFormStatus(false);
    } finally {
      setIsFetchingTest(false);
    }
  };

  const fetchCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const { data } = await axios({
        method: "get",
        url: GET_CATEGORIES,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
      });
      setAvailableCategories(data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      dispatch(
        snackbarStart({
          text: t("somethingWentWrong"),
          severity: "error",
        }),
      );
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleSaveTest = async () => {
    try {
      dispatch(start());
      const method = isEditMode ? "put" : "post";
      const url = isEditMode ? `${TESTS_API}/${testId}` : TESTS_API;

      await axios({
        method: method,
        url: url,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
        data: test,
      });
      setSelectedIds([]);
      dispatch(done());
      dispatch(
        snackbarStart({
          text: isEditMode
            ? t("testsDashboard.updatedMessage") || "Test successfully updated!"
            : t("testsDashboard.createdMessage"),
          severity: "success",
        }),
      );

      if (!isEditMode) {
        setTest({
          title: "",
          description: "",
          totalTimeSeconds: 6000,
          categoryId: "",
          questions: [],
          isPublic: false,
        });
      }

      setFormStatus(false);
      if (refreshData) refreshData();
    } catch (err) {
      console.log("Error saving test:", err);
      dispatch(done());
      dispatch(
        snackbarStart({
          text: t("somethingWentWrong"),
          severity: "error",
        }),
      );
    }
  };

  const onChecked = () =>
    setTest((prev) => ({ ...prev, isPublic: !prev.isPublic }));
  const handleTestInfoChange = (e) => {
    const { name, value } = e.target;
    setTest((prev) => ({
      ...prev,
      [name]: name === "totalTimeSeconds" ? parseInt(value, 10) * 60 : value,
    }));
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (err) => reject(err);
    });
  };

  const uploadImageToServer = async (file, onSuccess) => {
    const image = await readFileAsDataURL(file);
    try {
      setIsUploading(true);
      const { data } = await axios({
        method: "post",
        url: UPLOAD_IMAGE,
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        data: { image },
      });
      setIsUploading(false);
      onSuccess(data?.url);
      dispatch(
        snackbarStart({
          text: "Image uploaded successfully",
          severity: "success",
        }),
      );
    } catch (err) {
      setIsUploading(false);
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
      console.log(err);
    }
  };

  const handleQuestionImageUpload = async (questionId, e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadProgress((prev) => ({
        ...prev,
        [questionId]: { uploading: true, progress: 0 },
      }));
      await uploadImageToServer(file, (url) => {
        updateQuestion(questionId, "imageUrl", url);
        setUploadProgress((prev) => ({
          ...prev,
          [questionId]: { uploading: false, progress: 100 },
        }));
      });
    }
  };

  const handleOptionImageUpload = async (questionId, optionId, e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadProgress((prev) => ({
        ...prev,
        [`${questionId}-${optionId}`]: { uploading: true, progress: 0 },
      }));
      await uploadImageToServer(file, (url) => {
        updateOption(questionId, optionId, "imageUrl", url);
        setUploadProgress((prev) => ({
          ...prev,
          [`${questionId}-${optionId}`]: { uploading: false, progress: 100 },
        }));
      });
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q-${Date.now()}`,
      text: "",
      imageUrl: null,
      options: [
        { id: "a", text: "", imageUrl: null },
        { id: "b", text: "", imageUrl: null },
        { id: "c", text: "", imageUrl: null },
        { id: "d", text: "", imageUrl: null },
      ],
      correctAnswer: "a",
    };
    setTest((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const updateQuestion = (questionId, field, value) => {
    setTest((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, [field]: value } : q,
      ),
    }));
  };

  const updateOption = (questionId, optionId, field, value) => {
    setTest((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((opt) =>
                opt.id === optionId ? { ...opt, [field]: value } : opt,
              ),
            }
          : q,
      ),
    }));
  };

  const setCorrectAnswer = (questionId, optionId) => {
    setTest((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.id === questionId ? { ...q, correctAnswer: optionId } : q,
      ),
    }));
  };

  const deleteQuestion = (questionId) => {
    setTest((prev) => ({
      ...prev,
      questions: prev.questions.filter((q) => q.id !== questionId),
    }));
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const PlusIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );
  const TrashIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );
  const SaveIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
      <polyline points="17 21 17 13 7 13 7 21"></polyline>
      <polyline points="7 3 7 8 15 8"></polyline>
    </svg>
  );
  const ImageIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <circle cx="8.5" cy="8.5" r="1.5"></circle>
      <polyline points="21 15 16 10 5 21"></polyline>
    </svg>
  );
  const CheckIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
  const RefreshIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6"></path>
      <path d="M1 20v-6h6"></path>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
    </svg>
  );

  if (isFetchingTest) {
    return (
      <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center">
        <p className="text-gray-500 text-lg">{t("testForm.loadingData")}</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-[#030712] min-h-screen p-4 md:p-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto bg-white dark:bg-[#0f172a] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden transition-all duration-300">
        <div className="p-6 md:p-8 pb-0">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
              {isEditMode
                ? t("testForm.editTest")
                : t("testForm.createNewTest")}
            </h1>
            <button
              onClick={() => setFormStatus(false)}
              className="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-800 hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-500 rounded-lg transition-colors border border-transparent hover:border-rose-200 dark:hover:border-rose-900"
              title={t("testForm.closeBtn")}
            >
              {t("testForm.closeBtn")}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 mt-2">
          <button
            className={`px-6 py-4 font-semibold text-sm transition-all duration-200 border-b-2 ${
              activeTab === "basic"
                ? "border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            }`}
            onClick={() => setActiveTab("basic")}
          >
            {t("testForm.basicInfo")}
          </button>
          <button
            className={`px-6 py-4 font-semibold text-sm transition-all duration-200 border-b-2 ${
              activeTab === "questions"
                ? "border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
            }`}
            onClick={() => setActiveTab("questions")}
          >
            {t("testForm.questionsTab")} ({test.questions.length}/50)
          </button>
        </div>

        <div className="p-6 md:p-8">
          {/* Basic Info Tab */}
          {activeTab === "basic" && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t("testForm.testTitle")}
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={test.title}
                    onChange={handleTestInfoChange}
                    placeholder={t("testForm.testTitlePlaceholder")}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-900 dark:text-white outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t("testForm.timeLimit")}
                  </label>
                  <input
                    type="number"
                    name="totalTimeSeconds"
                    value={test.totalTimeSeconds / 60}
                    onChange={handleTestInfoChange}
                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-900 dark:text-white outline-none"
                    min="1"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 font-medium">
                    {t("testForm.testTime")}:{" "}
                    {formatTime(test.totalTimeSeconds)}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  {t("testForm.description")}
                </label>
                <textarea
                  name="description"
                  value={test.description}
                  onChange={handleTestInfoChange}
                  placeholder={t("testForm.descriptionPlaceholder")}
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-900 dark:text-white outline-none resize-y"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t("testForm.category")}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <select
                        name="categoryId"
                        value={test.categoryId}
                        onChange={(e) =>
                          setTest((prev) => ({
                            ...prev,
                            categoryId: e.target.value,
                          }))
                        }
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-900 dark:text-white outline-none appearance-none"
                        disabled={isLoadingCategories}
                      >
                        <option value="" disabled className="text-slate-400">
                          {t("testForm.selectCategory")}
                        </option>
                        {availableCategories.map((category) => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={fetchCategories}
                      className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-3 rounded-lg transition-colors flex items-center justify-center border border-slate-300 dark:border-slate-700 shadow-sm"
                      disabled={isLoadingCategories}
                      title="Refresh categories"
                    >
                      <RefreshIcon />
                    </button>
                  </div>
                  {isLoadingCategories && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                      {t("testForm.loadingCategories")}
                    </div>
                  )}
                </div>

                <div className="flex flex-col justify-center">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {t("testForm.visibility")}
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={test.isPublic}
                        onChange={onChecked}
                        className="sr-only"
                      />
                      <div
                        className={`block w-12 h-6 rounded-full transition-colors ${test.isPublic ? "bg-green-500" : "bg-slate-300 dark:bg-slate-700"}`}
                      ></div>
                      <div
                        className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${test.isPublic ? "transform translate-x-6" : ""}`}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                      {test.isPublic
                        ? t("testForm.publicVisible")
                        : t("testForm.privateHidden")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                <button
                  type="button"
                  onClick={() => setActiveTab("questions")}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg shadow-md hover:shadow-lg shadow-blue-500/20 font-semibold transition-all duration-200 text-sm flex items-center gap-2"
                >
                  {t("testForm.nextAddQuestions")}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Questions Tab */}
          {activeTab === "questions" && (
            <div className="space-y-6 animate-in fade-in duration-300">
              <div className="space-y-6">
                {test.questions.map((question, qIndex) => (
                  <div
                    key={question.id}
                    className="bg-white dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
                  >
                    <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                      <h3 className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 py-0.5 px-2.5 rounded-full text-xs">
                          {qIndex + 1}
                        </span>
                        {t("testForm.question")}
                      </h3>
                      <button
                        type="button"
                        onClick={() => deleteQuestion(question.id)}
                        className="text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 p-2 rounded-lg transition-colors"
                        title={t("testForm.deleteQuestion")}
                      >
                        <TrashIcon />
                      </button>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {t("testForm.questionText")}
                          </label>
                          <textarea
                            value={question.text}
                            onChange={(e) =>
                              updateQuestion(
                                question.id,
                                "text",
                                e.target.value,
                              )
                            }
                            className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-3 h-32 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-slate-900 dark:text-white outline-none resize-y"
                            placeholder={t("testForm.questionTextPlaceholder")}
                          />
                        </div>

                        <div className="space-y-4">
                          <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {t("testForm.questionImage")}
                          </label>
                          <div className="flex gap-4 h-32">
                            <label className="flex-1 border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl cursor-pointer flex flex-col items-center justify-center transition-colors group">
                              <div className="p-3 bg-white dark:bg-slate-800 rounded-full text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 shadow-sm mb-2 transition-colors">
                                <ImageIcon />
                              </div>
                              <span className="text-xs font-medium text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200">
                                {t("testForm.clickOrDragImage")}
                              </span>
                              <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={(e) =>
                                  handleQuestionImageUpload(question.id, e)
                                }
                                disabled={
                                  isUploading ||
                                  uploadProgress[question.id]?.uploading
                                }
                              />
                            </label>

                            {(question.imageUrl ||
                              uploadProgress[question.id]?.uploading) && (
                              <div className="w-32 h-32 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-900 relative">
                                {uploadProgress[question.id]?.uploading ? (
                                  <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                                    <div className="w-8 h-8 border-4 border-slate-200 dark:border-slate-700 border-t-blue-500 rounded-full animate-spin mb-2"></div>
                                    <span className="text-[10px] text-slate-500 font-medium">
                                      {t("testForm.uploading")}
                                    </span>
                                  </div>
                                ) : (
                                  <img
                                    src={question.imageUrl}
                                    alt="Question preview"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Options Section */}
                      <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
                        <label className="flex text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 items-center justify-between">
                          <span>{t("testForm.answerOptions")}</span>
                          <span className="text-xs font-normal text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                            {t("testForm.selectRadioButton")}
                          </span>
                        </label>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                          {question.options.map((option) => {
                            const isCorrect =
                              question.correctAnswer === option.id;
                            return (
                              <div
                                key={option.id}
                                className={`flex gap-3 p-3 rounded-lg border-2 transition-all duration-200 ${
                                  isCorrect
                                    ? "border-green-500 bg-green-50/50 dark:bg-green-500/10 dark:border-green-500/50"
                                    : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 hover:border-blue-300 dark:hover:border-slate-600"
                                }`}
                              >
                                <div className="pt-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCorrectAnswer(question.id, option.id)
                                    }
                                    className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${
                                      isCorrect
                                        ? "bg-green-500 border-green-500 text-white"
                                        : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 hover:border-slate-400"
                                    }`}
                                  >
                                    {isCorrect && <CheckIcon />}
                                  </button>
                                </div>
                                <div className="flex-1 flex flex-col gap-2">
                                  <div className="flex items-center">
                                    <span className="w-8 flex-shrink-0 font-bold text-slate-400 dark:text-slate-500">
                                      {option.id.toUpperCase()}.
                                    </span>
                                    <input
                                      type="text"
                                      value={option.text}
                                      onChange={(e) =>
                                        updateOption(
                                          question.id,
                                          option.id,
                                          "text",
                                          e.target.value,
                                        )
                                      }
                                      className="flex-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 dark:text-white"
                                      placeholder={t(
                                        "testForm.optionTextPlaceholder",
                                      )}
                                    />
                                  </div>
                                  <div className="flex gap-2 pl-8 h-10">
                                    <label className="flex-1 border border-dashed border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex items-center justify-center transition-colors">
                                      <ImageIcon />
                                      <span className="ml-2 text-xs font-medium text-slate-600 dark:text-slate-400">
                                        {t("testForm.addImage")}
                                      </span>
                                      <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={(e) =>
                                          handleOptionImageUpload(
                                            question.id,
                                            option.id,
                                            e,
                                          )
                                        }
                                        disabled={
                                          isUploading ||
                                          uploadProgress[
                                            `${question.id}-${option.id}`
                                          ]?.uploading
                                        }
                                      />
                                    </label>

                                    {(option.imageUrl ||
                                      uploadProgress[
                                        `${question.id}-${option.id}`
                                      ]?.uploading) && (
                                      <div className="w-10 h-10 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden bg-slate-50 dark:bg-slate-900 flex-shrink-0">
                                        {uploadProgress[
                                          `${question.id}-${option.id}`
                                        ]?.uploading ? (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <div className="w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                                          </div>
                                        ) : (
                                          <img
                                            src={option.imageUrl}
                                            alt="Option"
                                            className="w-full h-full object-cover"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {test.questions.length < 50 && (
                <button
                  type="button"
                  onClick={addQuestion}
                  className="w-full border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-blue-400 dark:hover:border-blue-500 text-slate-600 dark:text-slate-400 p-6 rounded-xl transition-all duration-200 flex flex-col items-center justify-center gap-2 group"
                >
                  <div className="p-3 bg-white dark:bg-slate-800 rounded-full shadow-sm group-hover:text-blue-500 transition-colors">
                    <PlusIcon />
                  </div>
                  <span className="font-semibold text-sm group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                    {t("testForm.addNewQuestion")} ({test.questions.length}/50)
                  </span>
                </button>
              )}

              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 mt-6 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setActiveTab("basic")}
                  className="w-full sm:w-auto px-6 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 font-semibold transition-colors duration-200 text-sm shadow-sm"
                >
                  {t("testForm.backToBasicInfo")}
                </button>
                <div className="flex w-full sm:w-auto gap-3">
                  <button
                    type="button"
                    onClick={() => setFormStatus(false)}
                    className="flex-1 sm:flex-none px-6 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-semibold transition-colors duration-200 border border-slate-200 dark:border-slate-700 text-sm"
                  >
                    {t("testForm.cancelBtn")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTest}
                    className="flex-1 sm:flex-none bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-2.5 rounded-lg shadow-md hover:shadow-lg shadow-blue-500/20 font-semibold transition-all duration-200 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={test.questions.length === 0}
                  >
                    <SaveIcon />
                    {isEditMode
                      ? t("testForm.updateTest")
                      : t("testForm.createTest")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
